import React, { useState, useEffect } from 'react';
import { billingAPI } from '../services/api';

const UserBillingModal = ({ user, onClose, onInvoiceUpdated }) => {
  const [activeTab, setActiveTab] = useState('stats');
  const [loading, setLoading] = useState(true);
  const [billingStats, setBillingStats] = useState(null);
  const [billingRecords, setBillingRecords] = useState([]);
  const [invoiceRequests, setInvoiceRequests] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (user) {
      loadBillingData();
    }
  }, [user]);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all data in parallel
      const [statsResponse, recordsResponse, invoicesResponse] = await Promise.all([
        billingAPI.getUserStats(user.id),
        billingAPI.getUserRecords(user.id),
        billingAPI.getUserInvoices(user.id)
      ]);

      if (statsResponse.data.success) {
        setBillingStats(statsResponse.data.data);
      }

      if (recordsResponse.data.success) {
        setBillingRecords(recordsResponse.data.data.records || []);
      }

      if (invoicesResponse.data.success) {
        setInvoiceRequests(invoicesResponse.data.data.requests || []);
      }
    } catch (err) {
      console.error('Failed to load billing data:', err);
      setError('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInvoiceStatus = async (invoiceId, status) => {
    try {
      await billingAPI.updateInvoiceStatus(invoiceId, { status });
      setSuccessMessage('Invoice status updated successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
      loadBillingData();
      // Notify parent to refresh user list (to update badge)
      if (onInvoiceUpdated) {
        onInvoiceUpdated();
      }
    } catch (err) {
      console.error('Failed to update invoice status:', err);
      setErrorMessage(err.response?.data?.error?.message || 'Failed to update invoice status');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleUploadPDF = async (invoiceId, file) => {
    if (!file) {
      setErrorMessage('Please select a PDF file');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    if (file.type !== 'application/pdf') {
      setErrorMessage('Only PDF files are allowed');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage('File size must be less than 10MB');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    try {
      await billingAPI.uploadInvoicePDF(invoiceId, file);
      setSuccessMessage('Invoice PDF uploaded successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
      loadBillingData();
      // Notify parent to refresh user list (to update badge)
      if (onInvoiceUpdated) {
        onInvoiceUpdated();
      }
    } catch (err) {
      console.error('Failed to upload PDF:', err);
      setErrorMessage(err.response?.data?.error?.message || 'Failed to upload PDF');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled_by_user: 'bg-orange-100 text-orange-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusDisplayName = (status) => {
    const statusNames = {
      completed: 'Completed',
      pending: 'Pending',
      processing: 'Processing',
      failed: 'Failed',
      refunded: 'Refunded',
      rejected: 'Rejected',
      cancelled_by_user: 'Cancelled by User'
    };
    return statusNames[status] || status;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">Billing Details</h2>
            <p className="text-blue-100 text-sm mt-1">
              {user.full_name || user.email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-green-800 font-medium">{successMessage}</p>
              </div>
              <button
                onClick={() => setSuccessMessage('')}
                className="text-green-600 hover:text-green-800"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-800 font-medium">{errorMessage}</p>
              </div>
              <button
                onClick={() => setErrorMessage('')}
                className="text-red-600 hover:text-red-800"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
            </div>
          ) : (
            <>
              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => setActiveTab('stats')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${
                      activeTab === 'stats'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Statistics
                  </button>
                  <button
                    onClick={() => setActiveTab('records')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${
                      activeTab === 'records'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Payment Records ({billingRecords.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('invoices')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 ${
                      activeTab === 'invoices'
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Invoice Requests ({invoiceRequests.length})
                  </button>
                </nav>
              </div>

              {/* Statistics Tab */}
              {activeTab === 'stats' && billingStats && (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600">Total Spent</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatCurrency(billingStats.total_spent)}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600">Total Payments</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {billingStats.total_payments}
                      </p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600">Pending Invoices</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {billingStats.pending_invoices}
                      </p>
                    </div>
                  </div>

                  {/* Payment Methods */}
                  {billingStats.payment_methods && billingStats.payment_methods.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment Methods</h3>
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payments</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {billingStats.payment_methods.map((method, index) => (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 capitalize">
                                  {method.payment_method}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {method.count}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {formatCurrency(method.total)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Recent Payments */}
                  {billingStats.recent_payments && billingStats.recent_payments.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Recent Payments</h3>
                      <div className="space-y-2">
                        {billingStats.recent_payments.map((payment) => (
                          <div key={payment.id} className="bg-gray-50 rounded-lg p-4 flex justify-between items-center">
                            <div>
                              <p className="font-medium text-gray-900">
                                {payment.plan_name || 'Unknown'} - {payment.billing_period || 'monthly'}
                              </p>
                              <p className="text-sm text-gray-500">{formatDate(payment.payment_date)}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">{formatCurrency(payment.amount)}</p>
                              <p className="text-sm text-gray-500 capitalize">{payment.payment_method}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Records Tab */}
              {activeTab === 'records' && (
                <div>
                  {billingRecords.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No payment records found</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment ID</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {billingRecords.map((record) => (
                            <tr key={record.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatDate(record.payment_date || record.created_at)}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900">
                                {record.description || `${record.plan_name || 'Unknown'} - ${record.billing_period || 'monthly'}`}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {formatCurrency(record.amount, record.currency)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                                {record.payment_method}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(record.payment_status)}`}>
                                  {record.payment_status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                                {record.payment_id || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Invoice Requests Tab */}
              {activeTab === 'invoices' && (
                <div>
                  {invoiceRequests.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No invoice requests found</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {invoiceRequests.map((invoice) => (
                        <div key={invoice.id} className="border border-gray-200 rounded-lg p-6">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900">{invoice.invoice_title}</h4>
                              <p className="text-sm text-gray-500 capitalize">Type: {invoice.invoice_type}</p>
                            </div>
                            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusBadgeClass(invoice.status)}`}>
                              {getStatusDisplayName(invoice.status)}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-gray-500">Amount</p>
                              <p className="text-sm font-medium text-gray-900">{formatCurrency(invoice.amount, invoice.currency)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Request Date</p>
                              <p className="text-sm font-medium text-gray-900">{formatDate(invoice.created_at)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Email</p>
                              <p className="text-sm font-medium text-gray-900">{invoice.email}</p>
                            </div>
                            {invoice.tax_id && (
                              <div>
                                <p className="text-xs text-gray-500">Tax ID</p>
                                <p className="text-sm font-medium text-gray-900">{invoice.tax_id}</p>
                              </div>
                            )}
                            {invoice.invoice_number && (
                              <div>
                                <p className="text-xs text-gray-500">Invoice Number</p>
                                <p className="text-sm font-medium text-gray-900">{invoice.invoice_number}</p>
                              </div>
                            )}
                          </div>

                          {invoice.address && (
                            <div className="mb-4">
                              <p className="text-xs text-gray-500">Address</p>
                              <p className="text-sm text-gray-900">
                                {invoice.address}
                                {invoice.city && `, ${invoice.city}`}
                                {invoice.state && `, ${invoice.state}`}
                                {invoice.postal_code && ` ${invoice.postal_code}`}
                                {invoice.country && `, ${invoice.country}`}
                              </p>
                            </div>
                          )}

                          {/* Current Invoice File */}
                          {invoice.invoice_file_url && (
                            <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                              <p className="text-xs text-green-700 mb-1">Invoice PDF Uploaded</p>
                              <a
                                href={`${new URL(process.env.REACT_APP_API_URL || 'http://localhost:9088/api').origin}${invoice.invoice_file_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 underline"
                              >
                                View Invoice PDF
                              </a>
                            </div>
                          )}

                          {/* Admin Actions */}
                          <div className="space-y-3 pt-4 border-t border-gray-200">
                            <div className="flex flex-wrap gap-2">
                              {invoice.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleUpdateInvoiceStatus(invoice.id, 'processing')}
                                    className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                                  >
                                    Mark Processing
                                  </button>
                                  <button
                                    onClick={() => handleUpdateInvoiceStatus(invoice.id, 'completed')}
                                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                                  >
                                    Mark Completed
                                  </button>
                                  <button
                                    onClick={() => handleUpdateInvoiceStatus(invoice.id, 'rejected')}
                                    className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                              {invoice.status === 'processing' && (
                                <button
                                  onClick={() => handleUpdateInvoiceStatus(invoice.id, 'completed')}
                                  className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                                >
                                  Mark Completed
                                </button>
                              )}
                            </div>

                            {/* PDF Upload Section */}
                            {(invoice.status === 'pending' || invoice.status === 'processing') && (
                              <div className="flex items-center gap-2">
                                <label className="flex-1 cursor-pointer">
                                  <span className="text-xs text-gray-600 block mb-1">Upload Invoice PDF</span>
                                  <div className="flex items-center">
                                    <span className="inline-block px-4 py-2 bg-purple-50 text-purple-700 text-sm font-semibold rounded hover:bg-purple-100 transition">
                                      Choose File
                                    </span>
                                    <span className="ml-3 text-sm text-gray-500">
                                      {invoice.invoice_file_url ? 'File uploaded' : 'No file chosen'}
                                    </span>
                                  </div>
                                  <input
                                    type="file"
                                    accept="application/pdf"
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        handleUploadPDF(invoice.id, file);
                                      }
                                    }}
                                    className="hidden"
                                  />
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserBillingModal;
